import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, IconButton, Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon        from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon          from '@mui/icons-material/RefreshRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon      from '@mui/icons-material/ReceiptLongRounded';
import CreditCardRoundedIcon       from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon         from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon      from '@mui/icons-material/PointOfSaleRounded';
import GavelRoundedIcon            from '@mui/icons-material/GavelRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import LocationOnRoundedIcon       from '@mui/icons-material/LocationOnRounded';
import TrendingUpRoundedIcon       from '@mui/icons-material/TrendingUpRounded';
import BarChartRoundedIcon         from '@mui/icons-material/BarChartRounded';
import PersonRoundedIcon           from '@mui/icons-material/PersonRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetOrders, APIGetFines } from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getInitials = (name = '') => {
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const MONEY_META = {
    transfer: { label: 'Перевод',   color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} /> },
    cash:     { label: 'Наличные',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} /> },
    terminal: { label: 'Терминал',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    '&:hover': { bgcolor: 'var(--joy-palette-background-level3)' },
};

// ════════════════════════════════════════════════════════════════════
//  WorkstationStatsPage
// ════════════════════════════════════════════════════════════════════
export default function WorkstationStatsPage() {
    const { token, onRefreshTick, activeShift } = useContext(AppContext);
    const navigate = useNavigate();

    // Период: по умолчанию текущий месяц
    const monthStart = useMemo(() => {
        const d = new Date(); return toISO(new Date(d.getFullYear(), d.getMonth(), 1));
    }, []);
    const today = useMemo(() => toISO(new Date()), []);

    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo,   setDateTo]   = useState(today);

    const [orders,  setOrders]  = useState([]);
    const [fines,   setFines]   = useState([]);
    const [loading, setLoading] = useState(true);

    // ── Загрузка ───────────────────────────────────────────────────
    const loadAll = useCallback(async () => {
        setLoading(true);
        const params = { date_from: dateFrom, date_to: dateTo };
        const [ordersData, finesData] = await Promise.all([
            APIGetOrders(token, params),
            APIGetFines(token, { ...params, is_active: true }),
        ]);
        if (Array.isArray(ordersData)) setOrders(ordersData);
        if (Array.isArray(finesData))  setFines(finesData);
        setLoading(false);
        return true;
    }, [token, dateFrom, dateTo]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // ── Подсчёты ───────────────────────────────────────────────────
    const stats = useMemo(() => {
        const sumOf = (arr) => arr.reduce((s, x) => s + parseFloat(x.value || 0), 0);

        const transfer = orders.filter(o => o.money_type === 'transfer');
        const cash     = orders.filter(o => o.money_type === 'cash');
        const terminal = orders.filter(o => o.money_type === 'terminal');

        const fineList      = fines.filter(f => !f.is_deduction);
        const deductionList = fines.filter(f => f.is_deduction);

        // Уникальные дни с продажами
        const uniqueDays = new Set(orders.map(o => o.date));

        // Средний чек
        const totalRev = sumOf(orders);
        const avgCheck = orders.length > 0 ? totalRev / orders.length : 0;

        return {
            totalRev,
            ordersCount: orders.length,
            avgCheck,
            workDays: uniqueDays.size,
            transferSum: sumOf(transfer),
            cashSum:     sumOf(cash),
            terminalSum: sumOf(terminal),
            transferCount: transfer.length,
            cashCount:     cash.length,
            terminalCount: terminal.length,
            fineSum:      sumOf(fineList),
            fineCount:    fineList.length,
            deductionSum: sumOf(deductionList),
            deductionCount: deductionList.length,
        };
    }, [orders, fines]);

    // ── Разбивка по фотографам ─────────────────────────────────────
    const byPhotographer = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            const id = o.photographer;
            if (!map.has(id)) {
                map.set(id, {
                    id, name: o.photographer_name || '—',
                    total: 0, count: 0,
                    transfer: 0, cash: 0, terminal: 0,
                });
            }
            const ph = map.get(id);
            const v = parseFloat(o.value || 0);
            ph.total += v;
            ph.count += 1;
            if (o.money_type === 'transfer') ph.transfer += v;
            else if (o.money_type === 'cash') ph.cash += v;
            else if (o.money_type === 'terminal') ph.terminal += v;
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [orders]);

    // ── Разбивка по дням ───────────────────────────────────────────
    const byDay = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            const key = o.date;
            if (!map.has(key)) map.set(key, { date: key, total: 0, count: 0 });
            const day = map.get(key);
            day.total += parseFloat(o.value || 0);
            day.count += 1;
        }
        return Array.from(map.values()).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }, [orders]);

    const maxDayTotal = useMemo(() => Math.max(...byDay.map(d => d.total), 1), [byDay]);

    // ── Разбивка по админам смен ───────────────────────────────────
    const byAdmin = useMemo(() => {
        const map = new Map();
        for (const o of orders) {
            if (!o.administrator || !o.administrator_name) continue;
            const id = o.administrator;
            if (!map.has(id)) {
                map.set(id, { id, name: o.administrator_name, total: 0, count: 0 });
            }
            const a = map.get(id);
            a.total += parseFloat(o.value || 0);
            a.count += 1;
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [orders]);

    // ── Быстрые периоды ────────────────────────────────────────────
    const quickPeriods = useMemo(() => {
        const today = new Date();
        const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const startOfPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 6);
        return [
            { label: 'Сегодня',      from: toISO(today),         to: toISO(today) },
            { label: 'Вчера',        from: toISO(yesterday),     to: toISO(yesterday) },
            { label: 'Неделя',       from: toISO(weekAgo),       to: toISO(today) },
            { label: 'Месяц',        from: toISO(startOfMonth),  to: toISO(today) },
            { label: 'Прошл. месяц', from: toISO(startOfPrevMonth), to: toISO(endOfPrevMonth) },
        ];
    }, []);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ────────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/workstation')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Статистика точки
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3 }}>
                                    <LocationOnRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                        {activeShift?.location?.name || activeShift?.location_name || '—'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <IconButton
                            variant="outlined" color="primary"
                            onClick={loadAll}
                            sx={{
                                // bgcolor: 'var(--joy-palette-background-level2)',
                                // border: '1px solid var(--joy-palette-divider)',
                            }}
                        >
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </motion.div>

                {/* ── Фильтры периода ──────────────────────────────── */}
                <Box sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 1.5, sm: 2 }, mb: 2.5,
                }}>
                    <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
                        {quickPeriods.map(p => {
                            const active = dateFrom === p.from && dateTo === p.to;
                            return (
                                <Chip
                                    key={p.label} size="sm"
                                    variant={active ? 'solid' : 'outlined'} color={active ? 'primary' : 'neutral'}
                                    onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                                    sx={{
                                        cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5,
                                        ...(active ? {} : inputSx),
                                        "&:hover": {
                                            color: active ? 'white' : 'black'
                                        }
                                    }}
                                >
                                    {p.label}
                                </Chip>
                            );
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 48 }}>
                            Период
                        </Typography>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            sx={{ ...inputSx, flex: 1, minWidth: 140 }}
                        />
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>—</Typography>
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            sx={{ ...inputSx, flex: 1, minWidth: 140 }}
                        />
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Главные метрики ─────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-primary-500)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 2, sm: 2.5 },
                                    background: 'linear-gradient(135deg, rgba(162,38,226,0.10) 0%, rgba(162,38,226,0.02) 100%)',
                                }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                        Выручка за период
                                    </Typography>
                                    <Typography sx={{ fontSize: 36, fontWeight: 900, color: 'var(--joy-palette-primary-300)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                        {fmtMoney(stats.totalRev)} ₽
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 1.2, flexWrap: 'wrap' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Чеков
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {stats.ordersCount}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Средний чек
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {fmtMoney(stats.avgCheck)} ₽
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Рабочих дней
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {stats.workDays}
                                            </Typography>
                                        </Box>
                                        {stats.workDays > 0 && (
                                            <Box>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    В день
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>
                                                    {fmtMoney(stats.totalRev / stats.workDays)} ₽
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </motion.div>

                            {/* ── По типам оплаты ─────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                    По типам оплаты
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                    {[
                                        { key: 'transfer', sum: stats.transferSum, count: stats.transferCount },
                                        { key: 'cash',     sum: stats.cashSum,     count: stats.cashCount },
                                        { key: 'terminal', sum: stats.terminalSum, count: stats.terminalCount },
                                    ].map(t => {
                                        const meta = MONEY_META[t.key];
                                        const pct = stats.totalRev > 0 ? (t.sum / stats.totalRev * 100) : 0;
                                        return (
                                            <Box key={t.key} sx={{
                                                px: 2, py: 1.8,
                                                borderRadius: 'var(--joy-radius-lg)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                bgcolor: meta.bg,
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Box sx={{ color: meta.color }}>{meta.icon}</Box>
                                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
                                                        {pct.toFixed(0)}%
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 20, fontWeight: 800, color: meta.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtMoney(t.sum)} ₽
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                    {meta.label}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                    {t.count} чек.
                                                </Typography>
                                                {/* мини-прогрессбар */}
                                                <Box sx={{
                                                    mt: 1, height: 4, borderRadius: 2,
                                                    bgcolor: 'var(--joy-palette-background-level2)', overflow: 'hidden',
                                                }}>
                                                    <Box sx={{
                                                        height: '100%', width: `${pct}%`,
                                                        bgcolor: meta.color,
                                                        transition: 'width .5s ease',
                                                    }} />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </motion.div>

                            {/* ── Штрафы и списания ────────────────── */}
                            {(stats.fineCount > 0 || stats.deductionCount > 0) && (
                                <motion.div variants={fadeUp}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                                        Штрафы и списания
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                                        <Box sx={{
                                            px: 2, py: 1.8,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            bgcolor: 'rgba(239,68,68,0.08)',
                                        }}>
                                            <Box sx={{ color: '#ef4444', mb: 0.5 }}>
                                                <GavelRoundedIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#ef4444', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                −{fmtMoney(stats.fineSum)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                Штрафы
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                {stats.fineCount} шт.
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            px: 2, py: 1.8,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            bgcolor: 'rgba(245,158,11,0.08)',
                                        }}>
                                            <Box sx={{ color: '#f59e0b', mb: 0.5 }}>
                                                <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#f59e0b', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                −{fmtMoney(stats.deductionSum)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                                Списания
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                {stats.deductionCount} шт.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Топ фотографов ───────────────────── */}
                            {byPhotographer.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <TrendingUpRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Топ фотографов · {byPhotographer.length}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.8}>
                                            {byPhotographer.map((ph, idx) => {
                                                const pct = stats.totalRev > 0 ? (ph.total / stats.totalRev * 100) : 0;
                                                return (
                                                    <Box key={ph.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.2,
                                                        px: 1.5, py: 1,
                                                        borderRadius: 'var(--joy-radius-md)',
                                                        bgcolor: idx === 0 ? 'rgba(34,197,94,0.06)' : 'transparent',
                                                        transition: 'background .15s',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: 13, fontWeight: 800,
                                                            color: idx === 0 ? '#22c55e' : 'var(--joy-palette-text-tertiary)',
                                                            width: 22, textAlign: 'center',
                                                        }}>
                                                            {idx + 1}
                                                        </Typography>
                                                        <Avatar size="sm" sx={{
                                                            width: 30, height: 30, fontSize: 11, fontWeight: 700,
                                                            bgcolor: 'rgba(162,38,226,0.15)',
                                                            color: 'var(--joy-palette-primary-300)',
                                                            border: '1.5px solid var(--joy-palette-primary-500)',
                                                            flexShrink: 0,
                                                        }}>
                                                            {getInitials(ph.name)}
                                                        </Avatar>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {ph.name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                {ph.count} чек. · {pct.toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{
                                                            fontSize: 14, fontWeight: 800,
                                                            color: idx === 0 ? '#22c55e' : 'var(--joy-palette-text-primary)',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            flexShrink: 0,
                                                        }}>
                                                            {fmtMoney(ph.total)} ₽
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── По админам смен ────────────────────── */}
                            {byAdmin.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <PersonRoundedIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                По админам смен · {byAdmin.length}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.8}>
                                            {byAdmin.map((a) => {
                                                const pct = stats.totalRev > 0 ? (a.total / stats.totalRev * 100) : 0;
                                                return (
                                                    <Box key={a.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.2,
                                                        px: 1.5, py: 1,
                                                        borderRadius: 'var(--joy-radius-md)',
                                                        transition: 'background .15s',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        <Avatar size="sm" sx={{
                                                            width: 30, height: 30, fontSize: 11, fontWeight: 700,
                                                            bgcolor: 'rgba(59,130,246,0.15)',
                                                            color: '#3b82f6',
                                                            border: '1.5px solid #3b82f6',
                                                            flexShrink: 0,
                                                        }}>
                                                            {getInitials(a.name)}
                                                        </Avatar>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {a.name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                {a.count} чек. · {pct.toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{
                                                            fontSize: 14, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            flexShrink: 0,
                                                        }}>
                                                            {fmtMoney(a.total)} ₽
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── По дням (мини-график) ────────────── */}
                            {byDay.length > 1 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        p: { xs: 2, sm: 2.5 },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <BarChartRoundedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Выручка по дням
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            gap: 0.4,
                                            height: 120,
                                            mb: 1,
                                            overflowX: 'auto',
                                            pb: 1,
                                        }}>
                                            {byDay.map(d => {
                                                const h = Math.max(4, (d.total / maxDayTotal) * 100);
                                                return (
                                                    <Box
                                                        key={d.date}
                                                        title={`${fmtDate(d.date)} · ${fmtMoney(d.total)} ₽ · ${d.count} чек.`}
                                                        sx={{
                                                            minWidth: 18,
                                                            flex: '1 0 18px',
                                                            height: `${h}%`,
                                                            background: 'linear-gradient(180deg, var(--joy-palette-primary-400) 0%, var(--joy-palette-primary-600) 100%)',
                                                            borderRadius: '4px 4px 0 0',
                                                            cursor: 'pointer',
                                                            transition: 'all .15s',
                                                            '&:hover': { transform: 'scaleY(1.05)', filter: 'brightness(1.2)' },
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {fmtDate(byDay[0]?.date)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {fmtDate(byDay[byDay.length - 1]?.date)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}

                            {/* ── Пустое состояние ───────────────────── */}
                            {orders.length === 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{ textAlign: 'center', py: 8 }}>
                                        <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                                            Нет данных за выбранный период
                                        </Typography>
                                    </Box>
                                </motion.div>
                            )}

                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}